import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { finalize } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { MailDeliverySpeed, MailDeliverySpeedToString } from '../../../common/model/const/enum';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { FormControlValidators } from '../../../core/component/forms/form-control.validators';
import { MessageService } from '../../../core/service/message.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import {
  GetInvoiceDeliveryMethodIcon,
  InvoiceDeliveryMethod,
  InvoiceDeliveryMethodToString,
  LeaseAgreementInformationRowContentKind,
  LeaseAgreementInformationRowContentKindToString,
} from '../../../invoice/model/const/enum';
import { EmailProviderKind, EmailProviderTestRequest, ProfileSettings } from '../../model/profile-settings';
import { LogRecord } from '../../model/log-record';
import { ProfileManagementService } from '../../service/data/profile-management.service';
import { SystemOwnerSettingsLogService } from '../../service/data/system-owner-settings-log.service';
import { UserAccountService } from '../../service/user-account.service';
import { FormControlExFactory } from './../../../core/service/factory/form-control-ex-factory.service';
import { ColumnDefinition } from '../../../core/model/column-definition';
import { ColumnDefinitionService } from '../../../rent/service/data/column-definition.service';

@Component({
  selector: 'app-profile',
  templateUrl: './profile-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProfileSettingsComponent implements OnInit {
  constructor(
    private _cf: FormControlExFactory,
    private _fb: FormBuilder,
    private _service: ProfileManagementService,
    private _messageService: MessageService,
    private _nativeWindow: NativeWindowService,
    private readonly _accountService: UserAccountService,
    private readonly _logService: SystemOwnerSettingsLogService,
    columnDefinitionService: ColumnDefinitionService
  ) {
    this.logColumnDefinition = columnDefinitionService.create('Log');
  }

  private _dataContext: ProfileSettings;

  allowMail: FormControlEx;
  allowManual: FormControlEx;
  form: FormGroup;
  emailProviderTestRecipient: FormControlEx;
  isSendingEmailProviderTest: boolean = false;
  hasEInvoiceB2CEnabled: boolean = environment.hasEInvoiceB2CEnabled;
  hasAvyEnabled: boolean = false;
  hasKivraEnabled: boolean = false;
  logs: LogRecord[] = [];
  logColumnDefinition: ColumnDefinition[];
  private logsLoaded = false;
  private logsLoading = false;

  get showBoplatsSverigeSettings(): boolean {
    return this._dataContext?.useBoplatsSverige === true;
  }

  get hasSendGridApiKey(): boolean {
    return this._dataContext?.hasSendGridApiKey === true;
  }

  invoiceDeliveryMethods = [
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Mail), value: InvoiceDeliveryMethod.Mail, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Mail) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Manual), value: InvoiceDeliveryMethod.Manual, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Manual) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithPDF), value: InvoiceDeliveryMethod.EmailWithPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithPDF) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithoutPDF), value: InvoiceDeliveryMethod.EmailWithoutPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithoutPDF) },
  ];

  leaseAgreementInformationRowContentKinds = [
    {
      name: LeaseAgreementInformationRowContentKindToString(LeaseAgreementInformationRowContentKind.LeaseAgreement),
      value: LeaseAgreementInformationRowContentKind.LeaseAgreement,
    },
    {
      name: LeaseAgreementInformationRowContentKindToString(LeaseAgreementInformationRowContentKind.RentalId),
      value: LeaseAgreementInformationRowContentKind.RentalId,
    },
    {
      name: LeaseAgreementInformationRowContentKindToString(LeaseAgreementInformationRowContentKind.NoRentalId),
      value: LeaseAgreementInformationRowContentKind.NoRentalId,
    },
    {
      name: LeaseAgreementInformationRowContentKindToString(LeaseAgreementInformationRowContentKind.PeriodAddressRentalId),
      value: LeaseAgreementInformationRowContentKind.PeriodAddressRentalId,
    },
    {
      name: LeaseAgreementInformationRowContentKindToString(LeaseAgreementInformationRowContentKind.PeriodAddress),
      value: LeaseAgreementInformationRowContentKind.PeriodAddress,
    },
  ];

  mailDeliverySpeeds = [
    { name: MailDeliverySpeedToString(MailDeliverySpeed.Priority), value: MailDeliverySpeed.Priority },
    { name: MailDeliverySpeedToString(MailDeliverySpeed.Economy), value: MailDeliverySpeed.Economy },
  ];

  private _getFormGroup(): FormGroup {
    const form = this._fb.group({
      autoAttest: this._cf.create('PROFILE_SETTINGS_FORM.AUTO_ATTEST_LABEL', this._dataContext.autoAttest),
      displayOrganizationNo: this._cf.create(
        'PROFILE_SETTINGS_FORM.DISPLAY_ORGANIZATION_NO_LABEL',
        this._dataContext.displayOrganizationNo
      ),
      includeQRCodeInEmailBody: this._cf.create(
        'PROFILE_SETTINGS_FORM.INCLUDE_QRCODE_IN_EMAIL_BODY',
        this._dataContext.includeQRCodeInEmailBody
      ),
      useInvoiceRowGroup: this._cf.create('PROFILE_SETTINGS_FORM.USE_INVOICE_ROW_GROUP', this._dataContext.useInvoiceRowGroup),
      rentInvoiceCaption: this._cf.create('PROFILE_SETTINGS_FORM.INVOICE_LAYOUT_CAPTION', this._dataContext.rentInvoiceCaption),
      invoiceDeliveryMethod: this._cf.create('PROFILE_SETTINGS_FORM.INVOICE_DELIVERY_METHOD', this._dataContext.invoiceDeliveryMethod),
      leaseAgreementInformationRowContent: this._cf.create(
        'PROFILE_SETTINGS_FORM.LEASE_AGREEMENT_INFORMATION_ROW_CONTENT',
        this._dataContext.leaseAgreementInformationRowContent
      ),
      enableESignature: this._cf.create('PROFILE_SETTINGS_FORM.ENABLE_ESIGNATURE', this._dataContext.enableESignature),
      enableInvoicing: this._cf.create('PROFILE_SETTINGS_FORM.INVOICING_LABEL', this._dataContext.enableInvoicing),
      enableEInvoiceB2C: this._cf.create('PROFILE_SETTINGS_FORM.EINVOICEB2C_LABEL', this._dataContext.enableEInvoiceB2C),
      //enableUserFeatures: this._cf.create('PROFILE_SETTINGS_FORM.ENABLE_USER_FEATURES', this._dataContext.enableUserFeatures),
      removeMails: this._cf.create('CLEANUP_SETTINGS_FORM.REMOVE_MAILS_LABEL', this._dataContext.removeMails),
      removeTenants: this._cf.create('CLEANUP_SETTINGS_FORM.REMOVE_TENANTS_LABEL', this._dataContext.removeTenants),
      mailDeliverySpeed: this._cf.create('PROFILE_SETTINGS_FORM.INVOICE_DELIVERY_SPEED', this._dataContext.mailDeliverySpeed),
      emailSender: this._cf.create('COMMON.EMAIL_SENDER', this._dataContext.emailSender),
      useFastighetsagarnaDokument: this._cf.create(
        'PROFILE_SETTINGS_FORM.USE_FASTIGHETSAGARNA_DOKUMENT',
        this._dataContext.useFastighetsagarnaDokument
      ),
      fastighetsagarnaApiKey: this._cf.create(
        'PROFILE_SETTINGS_FORM.FASTIGHETSAGARNA_DOKUMENT_API_KEY',
        this._dataContext.fastighetsagarnaApiKey
      ),
      useAvyApi: this._cf.create('PROFILE_SETTINGS_FORM.USE_AVY_API', this._dataContext.useAvyApi),
      avyApiClientId: this._cf.create('PROFILE_SETTINGS_FORM.AVY_API_KEY', this._dataContext.avyApiClientId),
      avyApiClientSecret: this._cf.create('PROFILE_SETTINGS_FORM.AVY_API_SECRET', this._dataContext.avyApiClientSecret),
      inactivityTimeoutMinutes: this._cf.create('PROFILE_SETTINGS_FORM.INACTIVITY_TIMEOUT', this._dataContext.inactivityTimeoutMinutes),
      locationAccessControlActive: this._cf.create('PROFILE_SETTINGS_FORM.LOCATION_ACCESS', this._dataContext.locationAccessControlActive),
      automaticDefermentUpToAmount: this._cf.create(
        'PROFILE_SETTINGS_FORM.AUTO_DEFERMENT_MAX_AMOUNT',
        this._dataContext.automaticDefermentUpToAmount
      ),
      moveMissingAndAdditionalPaymentDecimalsToRounding: this._cf.create(
        'PROFILE_SETTINGS_FORM.TREAT_SMALL_INCORRECT_PAYMENTS_AS_ROUNDING',
        this._dataContext.moveMissingAndAdditionalPaymentDecimalsToRounding
      ),
      sendZeroInvoice: this._cf.create('PROFILE_SETTINGS_FORM.SEND_ZERO_INVOICE', this._dataContext.sendZeroInvoice),
      allowDuplicateSwedishSSN: this._cf.create('PROFILE_SETTINGS_FORM.ALLOW_DUPLICATE_SE_SSN', this._dataContext.allowDuplicateSwedishSSN),
      useAvy: this._cf.createHidden(this._dataContext.useAvy),
      homeQPassword: this._cf.create('USER.PASSWORD', this._dataContext.homeQPassword),
      homeQUsername: this._cf.create('USER.NAME', this._dataContext.homeQUsername),
      useHomeQ: this._cf.create('PROFILE_SETTINGS_FORM.USE_HOMEQ', this._dataContext.useHomeQ),
      senderOfMessages: this._cf.create('PROFILE_SETTINGS_FORM.SENDER_OF_MESSAGES', this._dataContext.senderOfMessages),
      defaultResponseTimeEsigningCase: this._cf.create(
        'PROFILE_SETTINGS_FORM.DEFAULT_RESPONSE_TIME_ESIGN_CASE',
        this._dataContext.defaultResponseTimeEsigningCase
      ),
      useBrf: this._cf.create('PROFILE_SETTINGS_FORM.USE_BRF', this._dataContext.useBrf),
      brfControlDataProviderName: this._cf.create(
        'PROFILE_SETTINGS_FORM.BRF_CONTROL_DATA_PROVIDER_NAME',
        this._dataContext.brfControlDataProviderName
      ),
      brfControlDataProviderEmail: this._cf.create(
        'PROFILE_SETTINGS_FORM.BRF_CONTROL_DATA_PROVIDER_EMAIL',
        this._dataContext.brfControlDataProviderEmail
      ),
      brfControlDataProviderPhone: this._cf.create(
        'PROFILE_SETTINGS_FORM.BRF_CONTROL_DATA_PROVIDER_PHONE',
        this._dataContext.brfControlDataProviderPhone
      ),
      brfControlDataProviderOrganizationNumber: this._cf.create(
        'PROFILE_SETTINGS_FORM.BRF_CONTROL_DATA_PROVIDER_ORGANIZATION_NUMBER',
        this._dataContext.brfControlDataProviderOrganizationNumber
      ),
      useMortgage: this._cf.create('PROFILE_SETTINGS_FORM.USE_MORTGAGE', this._dataContext.useMortgage),
      dhMarketPassword: this._cf.create('USER.PASSWORD', this._dataContext.dhMarketPassword),
      dhMarketUserName: this._cf.create('USER.NAME', this._dataContext.dhMarketUserName),
      useDhMarket: this._cf.create('PROFILE_SETTINGS_FORM.USE_DHMARKET', this._dataContext.useDhMarket),
      dhMarketBackOfficeDomain: this._cf.create(
        'LETTING.ADVERTISMENT.DHMARKET.BACKOFFICE_DOMAIN',
        this._dataContext.dhMarketBackOfficeDomain
      ),
      useBoplatsSverige: this._cf.create('PROFILE_SETTINGS_FORM.BOPLATS_SVERIGE_USE', this._dataContext.useBoplatsSverige),
      boplatsSverigeCustomerGuid: this._cf.create(
        'PROFILE_SETTINGS_FORM.BOPLATS_SVERIGE_CUSTOMERGUID',
        this._dataContext.boplatsSverigeCustomerGuid
      ),
      boplatsSverigeDescriptionUrlPathTemplate: this._cf.createWithOptions(
        'PROFILE_SETTINGS_FORM.BOPLATS_SVERIGE_DESCRIPTION_URL_PATH_TEMPLATE',
        this._dataContext.boplatsSverigeDescriptionUrlPathTemplate,
        {
          helpText: 'PROFILE_SETTINGS_FORM.HELP_TEXT.BOPLATS_SVERIGE_DESCRIPTION_URL_PATH_TEMPLATE',
        }
      ),
      useRopoOne: this._cf.create('PROFILE_SETTINGS_FORM.USE_ROPOONE', this._dataContext.useRopoOne),
      ropoOneCid: this._cf.create('PROFILE_SETTINGS_FORM.ROPOONE_CID', this._dataContext.ropoOneCid),
      ropoOneApiKey: this._cf.create('PROFILE_SETTINGS_FORM.ROPEONE_API_KEY', this._dataContext.ropoOneApiKey),
      useWebhook: this._cf.create('PROFILE_SETTINGS_FORM.USE_WEBHOOK', this._dataContext.useWebhook),
      webhookUrl: this._cf.createWithOptions('PROFILE_SETTINGS_FORM.WEBHOOK_URL', this._dataContext.webhookUrl, {
        validator: FormControlValidators.url(),
      }),
      useOwnSendGrid: this._cf.create('PROFILE_SETTINGS_FORM.USE_OWN_SENDGRID', this._dataContext.useOwnSendGrid),
      sendGridApiKey: this._cf.create('PROFILE_SETTINGS_FORM.SENDGRID_API_KEY', this._dataContext.sendGridApiKey),
      emailProviderSenderAddress: this._cf.createEmail(
        'PROFILE_SETTINGS_FORM.EMAIL_PROVIDER_SENDER_ADDRESS',
        this._dataContext.emailProviderSenderAddress
      ),
      emailProviderSenderName: this._cf.create(
        'PROFILE_SETTINGS_FORM.EMAIL_PROVIDER_SENDER_NAME',
        this._dataContext.emailProviderSenderName
      ),
      sendGridEventWebhookUrl: this._cf.createReadonly(
        'PROFILE_SETTINGS_FORM.SENDGRID_EVENT_WEBHOOK_URL',
        this._dataContext.sendGridEventWebhookUrl
      ),
    });

    const updateSendGridRequirements = (enabled: boolean): void => {
      (form.controls['sendGridApiKey'] as FormControlEx).required = enabled && !this._dataContext.hasSendGridApiKey;
      (form.controls['emailProviderSenderAddress'] as FormControlEx).required = enabled;
    };

    updateSendGridRequirements(this._dataContext.useOwnSendGrid);
    form.controls['useOwnSendGrid'].valueChanges.subscribe(value => updateSendGridRequirements(value === true));
    return form;
  }

  ngOnInit(): void {
    this._service.getProfileSettings().subscribe({
      next: data => {
        if (data) {
          this._dataContext = data;
          this.form = this._getFormGroup();
          this.emailProviderTestRecipient = this._cf.createEmailMandatory(
            'PROFILE_SETTINGS_FORM.EMAIL_PROVIDER_TEST_RECIPIENT',
            null,
            'PROFILE_SETTINGS_FORM.HELP_TEXT.EMAIL_PROVIDER_TEST_RECIPIENT'
          );

          if (this._dataContext.useAvy) {
            this.hasAvyEnabled = true;
            this.invoiceDeliveryMethods.push({
              name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Avy),
              value: InvoiceDeliveryMethod.Avy,
              icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Avy),
            });
          }

          if (this._dataContext.useKivra) {
            this.hasKivraEnabled = true;
            this.invoiceDeliveryMethods.push({
              name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Kivra),
              value: InvoiceDeliveryMethod.Kivra,
              icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Kivra),
            });
          }

        }
      },
      error: error => this._messageService.showError(error),
    });
  }

  onSubmit() {
    if (this.form.invalid || this.form.pristine) return;

    this._service.setProfileSettings(this.form.value).subscribe({
      next: () => {
        this._accountService.refreshProfileSettings();
        this._messageService.showSuccess();
        if (this.logsLoaded) this.loadLogs(true);
      },
      error: error => this._messageService.showError(error),
    });
  }

  testEmailProvider(): void {
    const apiKeyControl = this.form.controls['sendGridApiKey'];
    const senderAddressControl = this.form.controls['emailProviderSenderAddress'];
    const controlsToValidate = [this.emailProviderTestRecipient, apiKeyControl, senderAddressControl];
    controlsToValidate.forEach(control => control.markAsTouched());

    if (controlsToValidate.some(control => control.invalid)) return;

    const request: EmailProviderTestRequest = {
      recipientAddress: this.emailProviderTestRecipient.value,
      provider: EmailProviderKind.SendGrid,
      sendGridApiKey: apiKeyControl.value,
      senderAddress: senderAddressControl.value,
      senderName: this.form.controls['emailProviderSenderName'].value,
    };

    this.isSendingEmailProviderTest = true;
    this._service
      .testEmailProvider(request)
      .pipe(finalize(() => (this.isSendingEmailProviderTest = false)))
      .subscribe({
        next: () => this._messageService.showSuccess('PROFILE_SETTINGS_FORM.EMAIL_PROVIDER_TEST_SUCCESS'),
        error: error => this._messageService.showError(error),
      });
  }

  loadLogs(force = false): void {
    if (this.logsLoading || (this.logsLoaded && !force)) return;

    this.logsLoading = true;
    this._logService.getPagedList(1, 500).subscribe({
      next: result => {
        this.logs = result.items;
        this.logsLoaded = true;
        this.logsLoading = false;
      },
      error: error => {
        this.logsLoading = false;
        this._messageService.showError(error);
      },
    });
  }

  get isFDApiKeyButtonDisabled(): boolean {
    const fastighetsagarnaApiKey: string = this.form.controls['fastighetsagarnaApiKey'].value;
    if (fastighetsagarnaApiKey != null && fastighetsagarnaApiKey.length > 0) {
      return true;
    }
    return false;
  }

  sendToFastighetsagarnaApiKey() {
    this._nativeWindow.openTab('https://app.fastdok.se/login?ReturnUrl=/customer/keys');
  }
}
